import type { OrderLineItem } from "./firebaseClient";
import { formatInr } from "./priceFormat";

// Unset by default — no real ZenFresh EmailJS account exists yet (see
// .env.example). sendOrderConfirmationEmail is a permanent no-op until all
// three are set, same inert-until-configured contract as analytics.ts's
// GA_MEASUREMENT_ID and razorpayCheckout.ts's RAZORPAY_KEY_ID.
const SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID as string | undefined;
const TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string | undefined;
const PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string | undefined;

export interface OrderConfirmationEmailInput {
  orderRef: string;
  customerName: string;
  customerEmail: string;
  items: OrderLineItem[];
  total: number;
}

/**
 * Best-effort only — the Firestore order write (see writeOrder in
 * firebaseClient.ts) is the source of truth for whether an order was
 * placed; this never blocks or throws into the checkout flow, and is only
 * ever called on a successful (payment reported) outcome, never on
 * failed/cancelled.
 */
export async function sendOrderConfirmationEmail(order: OrderConfirmationEmailInput): Promise<void> {
  if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
    if (import.meta.env.DEV) {
      console.info("[emailNotifications] would send order confirmation (EmailJS not configured):", order);
    }
    return;
  }

  try {
    const emailjs = await import("@emailjs/browser");
    const itemsSummary = order.items.map((line) => `${line.name} x${line.qty} — ${formatInr(line.lineTotal)}`).join("\n");

    await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      {
        order_ref: order.orderRef,
        customer_name: order.customerName,
        to_email: order.customerEmail,
        items_summary: itemsSummary,
        total: formatInr(order.total),
      },
      { publicKey: PUBLIC_KEY }
    );
  } catch (err) {
    if (import.meta.env.DEV) {
      console.info("[emailNotifications] send failed (non-fatal):", err);
    }
  }
}
